import React, { FormEvent } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import * as yup from 'yup'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { AppLayout } from '@/components/layouts/AppLayout'

interface Props {
  notebook: {
    id: number
    name: string
    slug: string
    description: string | null
  }
}

const notebookSchema = yup.object({
  notebook: yup.object({
    name: yup.string().required('Notebook name is required').max(100, 'Notebook name must be at most 100 characters'),
  })
})

export default function Edit({ notebook }: Props) {
  const { data, setData, put, processing, errors, setError, clearErrors } = useForm({
    notebook: {
      name: notebook.name,
      description: notebook.description || '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      notebookSchema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    put(`/notebooks/notebooks/${notebook.slug}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete notebook '${notebook.name}'? This will delete all notes and folders. This cannot be undone.`)) {
      router.delete(`/notebooks/notebooks/${notebook.slug}`)
    }
  }

  return (
    <AppLayout title={`Edit notebook: ${notebook.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Notebook name"
            fullWidth
            autoFocus
            value={data.notebook.name}
            onChange={e => setData('notebook', { ...data.notebook, name: e.target.value })}
            error={'notebook.name' in errors}
            helperText={(errors as Record<string, string>)['notebook.name']}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            value={data.notebook.description}
            onChange={e => setData('notebook', { ...data.notebook, description: e.target.value })}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update notebook
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href={`/notebooks/notebooks/${notebook.slug}`} style={{ color: '#1a1a1a' }}>Back to notebook</Link>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDelete}
          >
            Delete this notebook
          </Button>
        </Box>
      </Paper>
    </AppLayout>
  )
}
